<script setup lang="ts">
/**
 * Правила приёма платежей.
 *
 * Отдельным документом, а не разделом оферты, по двум причинам. Первая:
 * на него смотрят банк и платёжная система при подключении эквайринга и
 * рекуррентных списаний — им нужен адрес, где написано, чем и как платят.
 * Вторая: раздел про автоплатёж должен читаться без остального договора, иначе
 * человек, у которого списались деньги, будет искать объяснение в двадцати
 * страницах про интеллектуальную собственность.
 */
import { formatRubles } from '@zetter/shared';
import { computed } from 'vue';
import LegalDocument from '../../components/legal/LegalDocument.vue';
import { legal } from '../../site/public-data.js';

const company = computed(() => legal.value?.company ?? null);
const terms = computed(() => legal.value?.terms ?? null);
const methods = computed(() => legal.value?.methods ?? null);

const minPayment = computed(() =>
  terms.value ? formatRubles(terms.value.minPaymentKopecks) : '—',
);
const maxPayment = computed(() =>
  terms.value ? formatRubles(terms.value.maxPaymentKopecks) : '—',
);
const bindingAmount = computed(() =>
  terms.value ? formatRubles(terms.value.bindingAmountKopecks) : '—',
);
</script>

<template>
  <LegalDocument
    title="Правила приёма платежей"
    revision="21 августа 2026 г."
    subtitle="Чем и как оплачиваются услуги сервиса Keypi, как работает подписка с автоматическим
      списанием и как вернуть деньги. Документ является неотъемлемой частью публичной оферты."
  >
    <h2 id="who">1. Кто принимает оплату</h2>
    <p>
      1.1. Получателем платежей является {{ company?.fullName || 'Исполнитель' }}<template
        v-if="company?.inn"
      >, ИНН {{ company.inn }}</template
      >. Полные реквизиты — в разделе 13
      <router-link to="/offer">публичной оферты</router-link>.
    </p>
    <p>
      1.2. Оплата производится в рублях Российской Федерации. Валютой расчётов является рубль; иные
      валюты к оплате не принимаются.
    </p>
    <p>
      1.3. Минимальная сумма пополнения баланса — {{ minPayment }}, максимальная сумма одного
      платежа — {{ maxPayment }}. Ограничения не распространяются на оплату тарифного плана: её
      сумму определяет сам план.
    </p>

    <h2 id="methods">2. Способы оплаты</h2>
    <p>2.1. Доступны следующие способы. Кто платит — определяет способ.</p>

    <h3>Платит физическое лицо</h3>
    <ul>
      <li v-if="methods?.card">
        <strong>Банковской картой</strong> — на платёжной странице банка-эквайера. Принимаются карты
        платёжных систем, поддерживаемых эквайером. Средства зачисляются в течение нескольких минут.
      </li>
      <li v-if="methods?.card">
        <strong>Системой быстрых платежей (СБП)</strong> — по QR-коду на той же платёжной странице.
        Оплата подтверждается в приложении банка плательщика.
      </li>
      <li>
        <strong>Списанием с привязанного способа оплаты</strong> — в один клик, без перехода на
        платёжную страницу. Условия привязки — раздел 4.
      </li>
    </ul>

    <h3>Платит организация</h3>
    <ul>
      <li v-if="methods?.b2b">
        <strong>По QR-коду СБП для бизнеса</strong> — организация платит со своего расчётного счёта
        из банковского приложения. Сумму и назначение платежа плательщик вводит сам по данным,
        показанным на экране оплаты. <strong>Код платежа в назначении обязателен</strong>: по нему
        поступление сопоставляется с организацией. Платёж без кода зачисляется после ручной
        проверки, что занимает до одного рабочего дня.
      </li>
      <li>
        <strong>По счёту на расчётный счёт</strong> — счёт формируется в разделе «Баланс» и
        оплачивается обычным платёжным поручением. Зачисление — по факту поступления средств,
        как правило в течение одного-двух рабочих дней. Для выставления счёта нужны заполненные
        реквизиты организации.
      </li>
    </ul>

    <h2 id="security">3. Безопасность платежей</h2>
    <p>
      3.1. Платёжные данные вводятся на стороне банка-эквайера. Номер карты, срок действия и код
      проверки подлинности Исполнителю не передаются и им не хранятся.
    </p>
    <p>
      3.2. Оплата картой проходит с проверкой 3-D Secure, если её требует банк-эмитент. Оплата по
      СБП подтверждается в приложении банка плательщика.
    </p>
    <p>
      3.3. Обмен данными с банком-эквайером ведётся по защищённому соединению, каждое сообщение
      подписывается криптографической подписью. Уведомление об оплате, не прошедшее проверку
      подписи, отклоняется.
    </p>
    <p>
      3.4. Кассовый чек формируется банком-эквайером в соответствии с Федеральным законом
      от 22.05.2003 № 54-ФЗ и направляется на адрес электронной почты, указанный при оплате.
    </p>

    <h2 id="recurring">4. Подписка и автоплатёж</h2>
    <p>
      4.1. Тарифный план оплачивается периодически: раз в месяц или раз в год. Чтобы не выставлять
      счёт каждый период, Заказчик может <strong>привязать способ оплаты</strong> — банковскую карту
      или счёт по СБП, — и тогда подписка продлевается списанием.
    </p>

    <h3>Как появляется привязка</h3>
    <p>
      4.2. Привязка создаётся только настоящим платежом: банк выдаёт разрешение на последующие
      списания после оплаты, подтверждённой держателем карты через 3-D Secure либо подтверждённой в
      приложении банка при оплате по СБП. Кнопки «сохранить карту» без оплаты не существует.
    </p>
    <p>
      4.3. Привязать способ оплаты можно двумя путями:
    </p>
    <ul>
      <li>
        отметить «запомнить способ оплаты» при обычной оплате — пополнении баланса или оплате
        плана. Отдельного списания при этом не возникает;
      </li>
      <li>
        нажать «Привязать» в разделе «Баланс». В этом случае проводится платёж на
        {{ bindingAmount }}, и <strong>эта сумма зачисляется на баланс Заказчика</strong> обычным
        пополнением — она не является платой за привязку.
      </li>
    </ul>
    <p>
      4.4. Данные привязанной карты Исполнителю не передаются. Хранится идентификатор, выданный
      банком; расплатиться им можно только в пользу Исполнителя и только через его расчётный счёт.
    </p>

    <h3>Что и когда списывается</h3>
    <p>
      4.5. <strong>Продление подписки.</strong> Списание производится в день окончания оплаченного
      периода. Сумма — стоимость следующего периода по действующему тарифному плану, включая
      оплаченные расширения. Периодичность — раз в месяц либо раз в год, в зависимости от выбранного
      периода.
    </p>
    <p>
      4.6. <strong>Автопополнение баланса.</strong> Если Заказчик включил его и указал сумму,
      списание производится, когда баланс опускается до заданного Заказчиком порога. Сумма и порог
      задаются Заказчиком и меняются им в любой момент. Между двумя автопополнениями выдерживается
      не менее часа.
    </p>
    <p>
      4.7. <strong>Разовое пополнение по кнопке.</strong> Списание производится по прямому действию
      Заказчика на указанную им сумму.
    </p>
    <p>
      4.8. Иных списаний по привязке не производится. Об изменении стоимости плана Исполнитель
      сообщает до начала нового периода; продолжение подписки после изменения означает согласие с
      новой стоимостью.
    </p>

    <h3>Как отказаться</h3>
    <p>
      4.9. Отвязать способ оплаты можно в любой момент в разделе «Баланс» → «Автоплатёж» →
      «Отвязать». После отвязки списания прекращаются немедленно.
    </p>
    <p>
      4.10. При отвязке счёта, привязанного по СБП, Исполнитель отзывает разрешение и на стороне
      банка. Плательщик может отозвать его и самостоятельно — в приложении своего банка, в разделе
      подписок и разрешений; в этом случае Исполнитель прекращает списания, получив уведомление
      банка.
    </p>
    <p>
      4.11. Отдельно от привязки отключается само автопродление: подписка перестанет продлеваться,
      но привязка сохранится для пополнения баланса.
    </p>
    <p>
      4.12. Отказ от автоплатежа не прекращает оплаченный период: он действует до конца.
    </p>

    <h3>Если списание не прошло</h3>
    <p>
      4.13. При отказе банка Исполнитель выставляет счёт на продление и сохраняет действие подписки
      ещё {{ terms?.graceDays ?? 7 }} дней. Если оплата не поступает, организация переводится на
      бесплатный план: данные Заказчика сохраняются, снижаются только ограничения.
    </p>
    <p>
      4.14. После нескольких неудачных списаний подряд привязка отключается, и Заказчику
      предлагается привязать способ оплаты заново.
    </p>

    <h2 id="crediting">5. Момент оплаты и зачисление</h2>
    <p>
      5.1. Обязательство по оплате считается исполненным с момента поступления денежных средств
      Исполнителю.
    </p>
    <p>
      5.2. Средства зачисляются на баланс организации автоматически по уведомлению банка. Если
      уведомление не поступило, зачисление производится по банковской выписке — обычно в течение
      нескольких минут, но не позднее одного рабочего дня.
    </p>
    <p>
      5.3. Зачисляется фактически поступившая сумма. При оплате по QR-коду или платёжным поручением
      сумму вводит плательщик, и она может отличаться от суммы счёта; баланс изменяется на ту сумму,
      которая поступила.
    </p>
    <p>
      5.4. Если платёж не зачислен в указанный срок, Заказчику следует обратиться по адресу,
      указанному в разделе 8, приложив подтверждение платежа. Возврат ошибочных платежей —
      по разделу 6.
    </p>

    <h2 id="refund">6. Возврат средств</h2>
    <p>
      6.1. Возврату подлежит неиспользованный остаток средств, фактически внесённых Заказчиком.
      Бонусы, приветственные и включённые в тарифный план кредиты денежными средствами не являются
      и не возвращаются.
    </p>
    <p>
      6.2. Стоимость тарифного плана за начатый период не возвращается: ограничения и включённый
      объём предоставлены на весь период.
    </p>
    <p>
      6.3. Заявление на возврат направляется с адреса электронной почты владельца организации на
      адрес из раздела 8 и содержит наименование организации, дату и сумму платежа.
    </p>
    <p>
      6.4. Срок рассмотрения заявления — до десяти рабочих дней. Возврат производится
      <strong>тем же способом, которым была произведена оплата</strong>: на ту же карту, тот же счёт
      по СБП или тот же расчётный счёт. Возврат на реквизиты третьих лиц не производится.
    </p>
    <p>
      6.5. Срок зачисления возврата определяется банком плательщика и составляет, как правило, до
      десяти рабочих дней с момента отправки средств Исполнителем.
    </p>

    <h2 id="disputes">7. Спорные ситуации</h2>
    <p>
      7.1. При двойном списании, списании неверной суммы или списании после отвязки Заказчик
      сообщает об этом по адресу из раздела 8. Ошибочно списанные средства возвращаются в полном
      объёме в срок, установленный п. 6.4, без удержаний.
    </p>
    <p>
      7.2. Расшифровка всех платежей и списаний доступна Заказчику в Сервисе: разделы «Счета»,
      «Списания» и «История баланса».
    </p>

    <h2 id="contacts">8. Контакты</h2>
    <dl v-if="company">
      <dt>Получатель платежей</dt>
      <dd>{{ company.fullName || company.name || '—' }}</dd>
      <dt>ИНН</dt>
      <dd>{{ company.inn || '—' }}</dd>
      <dt>Адрес</dt>
      <dd>{{ company.address || '—' }}</dd>
      <dt>Электронная почта</dt>
      <dd>{{ company.email || '—' }}</dd>
      <template v-if="company.phone">
        <dt>Телефон</dt>
        <dd>{{ company.phone }}</dd>
      </template>
    </dl>
    <p v-else class="text-gray-500">Реквизиты загружаются…</p>
  </LegalDocument>
</template>
